<template>
  <div class="vx-row" v-if="request">
    <!-- Linha do tempo -->
    <div class="vx-col lg:w-3/5 w-full">
      <vx-card>
        <div class="flex justify-between items-center mb-4">
          <h4 class="mb-0">{{ $t('cancellation.timeline') }} · {{ request.protocol }}</h4>
          <vs-chip transparent :color="statusColor(request.status)" class="text-base">
            {{ $t('cancellation.request_status_.' + request.status) }}
          </vs-chip>
        </div>

        <app-timeline>
          <app-timeline-item
            v-for="event in request.events"
            :key="event.id"
            :title="event.title"
            :subtitle="event.description || ''"
            :time="formatDateTime(event.happened_at)"
            :icon="eventIcon(event.event_type)"
            :variant="eventVariant(event.event_type)"/>
        </app-timeline>
      </vx-card>
    </div>

    <!-- Política congelada + decisão -->
    <div class="vx-col lg:w-2/5 w-full">
      <vx-card class="mb-4">
        <h4 class="mb-3">{{ $t('cancellation.policy_at_booking') }}</h4>
        <div class="border rounded p-1">
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('cancellation.version') }}</small>
            <small v-if="request.policy_version">
              {{ policyName }} v{{ request.policy_version.version }}
            </small>
            <small v-else>—</small>
          </div>
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('cancellation.applied_tier') }}</small>
            <small>{{ tierLabel }}</small>
          </div>
          <!--
            O motivo passa a aparecer aqui. É esta tela que dispara o registro em
            Privacidade → Log de Acesso quando o motivo é médico — e antes disto o
            log afirmava um acesso a dado de saúde numa tela que não mostrava dado
            de saúde nenhum. Ou o dado aparece, ou o log mente.
          -->
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('cancellation.reason') }}</small>
            <small v-if="isMedicalReason" class="text-danger font-weight-bold">
              {{ $t('cancellation.reason_.ME') }}
            </small>
            <small v-else>
              {{ request.reason_text || $t('cancellation.reason_.' + request.reason) }}
            </small>
          </div>
          <div v-if="isMedicalReason" class="mb-1">
            <small class="text-muted">
              Dado de saúde (LGPD, Art. 5º, II). Use apenas para decidir este pedido.
              Nenhuma descrição é guardada — peça o atestado ao aluno se precisar.
            </small>
          </div>
          <div class="flex justify-between mb-1">
            <small class="text-muted">{{ $t('cancellation.medical_exception') }}</small>
            <small v-if="medicalEnabled" class="text-success">Habilitada</small>
            <small v-else class="text-danger">
              {{ $t('cancellation.not_enabled_in') }}
              v{{ request.policy_version ? request.policy_version.version : '—' }}
            </small>
          </div>
        </div>
      </vx-card>

      <vx-card>
        <h4 class="mb-3">{{ $t('cancellation.decision') }}</h4>

        <b-form-group>
          <b-form-radio-group
            v-model="decision.decision"
            :options="decisionOptions"
            stacked
            class="required"/>
        </b-form-group>

        <b-form-group :label="$t('cancellation.absorbed_by')">
          <b-form-select v-model="decision.absorbed_by" :options="absorbedByOptions" class="required"/>
        </b-form-group>

        <b-form-group :label="$t('cancellation.justification')">
          <b-form-textarea
            v-model="decision.justification"
            :placeholder="$t('cancellation.justification_placeholder')"
            rows="3"/>
        </b-form-group>

        <div class="flex gap-4">
          <b-button
            v-permission="'cancellation_movements.edit'"
            variant="primary"
            class="mr-2"
            :disabled="!decision.decision || !decision.absorbed_by"
            @click="decide">
            {{ $t('cancellation.decide_execute') }}
          </b-button>
          <b-button variant="outline-secondary" @click="askMoreInfo">
            {{ $t('cancellation.ask_more_info') }}
          </b-button>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BFormGroup,
  BFormRadioGroup,
  BFormSelect,
  BFormTextarea
} from 'bootstrap-vue'
import moment from 'moment'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import { currencyFormatter } from '@/util/Util'

import CancellationRequestService from '@/services/api/CancellationRequestService'

export default {
  components: {
    AppTimeline,
    AppTimelineItem,
    BButton,
    BFormGroup,
    BFormRadioGroup,
    BFormSelect,
    BFormTextarea
  },

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    service: null,
    request: null,
    decision: {
      decision: 'RF',
      absorbed_by: 'ES',
      justification: ''
    }
  }),

  computed: {
    policyName() {
      return this._.get(this.request, 'policy_version.policy.name', '')
    },
    tierLabel() {
      const tier = this.request ? this.request.tier : null
      if (!tier) return '—'
      const range = tier.max_hours === null
        ? `mais de ${Number(tier.min_hours)}h`
        : (!tier.min_hours ? `menos de ${Number(tier.max_hours)}h` : `${Number(tier.min_hours)}h a ${Number(tier.max_hours)}h`)
      return `${range} · ${this.$t('cancellation.remedy_.' + this.request.remedy).toLowerCase()}`
    },
    medicalEnabled() {
      const exceptions = this._.get(this.request, 'policy_version.manual_exceptions') || {}
      return !!exceptions.medical_certificate
    },
    /** Motivo médico é dado sensível — muda o destaque e o aviso na tela. */
    isMedicalReason() {
      return this.request && this.request.reason === 'ME'
    },
    decisionOptions() {
      const amount = this.request ? currencyFormatter(this.request.amount) : ''
      return [
        { value: 'RF', text: `${this.$t('cancellation.decision_.RF')} · ${amount}` },
        { value: 'KC', text: this.$t('cancellation.decision_.KC') },
        { value: 'SC', text: this.$t('cancellation.decision_.SC') }
      ]
    },
    absorbedByOptions() {
      return ['ES', 'HU', 'SH'].map(value => ({
        value,
        text: this.$t('cancellation.absorbed_by_.' + value)
      }))
    }
  },

  methods: {
    formatDateTime(value) {
      return moment(value).format('DD/MM · HH[h]mm')
    },
    statusColor(status) {
      return { CO: 'warning', HA: 'primary', FI: 'success' }[status] || 'secondary'
    },
    eventIcon(type) {
      return {
        created: 'FileTextIcon',
        requested: 'XCircleIcon',
        engine: 'CpuIcon',
        contested: 'FlagIcon',
        club_reply: 'SlashIcon',
        club_approval: 'CheckCircleIcon',
        club_denial: 'SlashIcon',
        escalated: 'ClockIcon',
        info_requested: 'HelpCircleIcon',
        hubee_decision: 'AwardIcon',
        club_cancel: 'XCircleIcon',
        reschedule_offered: 'RepeatIcon',
        rescheduled: 'RepeatIcon'
      }[type] || 'CircleIcon'
    },
    eventVariant(type) {
      return {
        created: 'secondary',
        requested: 'primary',
        engine: 'success',
        contested: 'warning',
        club_reply: 'danger',
        club_approval: 'success',
        club_denial: 'danger',
        escalated: 'primary',
        info_requested: 'info',
        hubee_decision: 'success',
        club_cancel: 'danger',
        reschedule_offered: 'info',
        rescheduled: 'info'
      }[type] || 'secondary'
    },
    loadData() {
      this.$vs.loading()
      this.service.detail(this.id).then(
        request => { this.request = request },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    },
    decide() {
      this.$vs.loading()
      this.service.decide(this.id, this.decision).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.$router.push('/cancellations/movements')
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    },
    askMoreInfo() {
      this.$vs.loading()
      this.service.respond(this.id, this.decision.justification || 'Hubee pediu mais informação', true).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.loadData()
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    }
  },

  beforeMount() {
    this.service = CancellationRequestService.build(this.$vs)
  },

  mounted() {
    this.loadData()
  }
}
</script>
